import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Modal, ModalBody, ModalHeader, Spinner, Button } from "reactstrap";
import {
  CanvaDesignItem,
  createCanvaExportAndGetUrls,
  fetchCanvaDesigns,
} from "../../../../cores/canva/connectApi";
import { IOnDesignPublish } from "../../../../cores/canva/interfaces/canva";
import { CanvaMark } from "../CanvaMark";
import "./CanvaDesignPickerModal.scss";

type Props = {
  isOpen: boolean;
  toggle: () => void;
  onExported: (payload: IOnDesignPublish) => void | Promise<void>;
};

export const CanvaDesignPickerModal = ({
  isOpen,
  toggle,
  onExported,
}: Props) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [items, setItems] = useState<CanvaDesignItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [continuation, setContinuation] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) {
      setItems([]);
      setContinuation(undefined);
      setError(null);
      setExportingId(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchCanvaDesigns({ limit: 30 });
        if (!cancelled) {
          setItems(res.items);
          setContinuation(res.continuation);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message ||
              e?.message ||
              intl.formatMessage({ id: "app.canvaConnect.loadDesignsError" })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, intl]);

  const loadMore = useCallback(async () => {
    if (!continuation) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await fetchCanvaDesigns({
        limit: 30,
        continuation,
      });
      setItems((prev) => [...prev, ...res.items]);
      setContinuation(res.continuation);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({ id: "app.canvaConnect.loadDesignsError" })
      );
    } finally {
      setLoadingMore(false);
    }
  }, [continuation, intl]);

  const handlePick = async (design: CanvaDesignItem) => {
    setExportingId(design.id);
    setError(null);
    try {
      const urls = await createCanvaExportAndGetUrls({
        designId: design.id,
        format: "jpg",
        quality: 90,
      });
      const exportUrl = urls[0];
      if (!exportUrl) {
        throw new Error("No download URL");
      }
      await onExported({
        exportUrl,
        designTitle: design.title || "Canva",
      });
      toggle();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({ id: "app.canvaConnect.exportError" })
      );
    } finally {
      setExportingId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      className="canva-design-picker-dialog"
      modalClassName="canva-design-picker-modal"
      zIndex={2000}
      scrollable
    >
      <ModalHeader toggle={toggle}>
        <span className="d-inline-flex align-items-center">
          <span className="mr-2 d-inline-flex">
            <CanvaMark size={26} />
          </span>
          {intl.formatMessage({ id: "app.canvaConnect.pickDesignTitle" })}
        </span>
      </ModalHeader>
      <ModalBody>
        {error && (
          <div className="alert alert-danger py-2 small mb-3" role="alert">
            {error}
          </div>
        )}
        {loading && items.length === 0 ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="canva-design-picker__grid">
            {items.map((d) => (
              <button
                key={d.id}
                type="button"
                className="canva-design-picker__tile"
                disabled={!!exportingId}
                onClick={() => handlePick(d)}
              >
                <div className="canva-design-picker__thumb-wrap">
                  {d.thumbnail?.url ? (
                    <img
                      src={d.thumbnail.url}
                      alt=""
                      className="canva-design-picker__thumb"
                    />
                  ) : (
                    <div className="canva-design-picker__placeholder" />
                  )}
                  {exportingId === d.id && (
                    <div className="canva-design-picker__overlay">
                      <Spinner size="sm" color="light" />
                    </div>
                  )}
                </div>
                <div className="canva-design-picker__title" title={d.title}>
                  {d.title}
                </div>
              </button>
            ))}
          </div>
        )}
        {continuation && !loading && (
          <div className="text-center mt-3">
            <Button
              color="link"
              type="button"
              onClick={() => loadMore()}
              disabled={!!exportingId || loadingMore}
            >
              {loadingMore ? (
                <Spinner size="sm" color="primary" />
              ) : (
                intl.formatMessage({ id: "app.canvaConnect.loadMore" })
              )}
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
